/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CrudOverviewBaseComponent } from '../../../base/crud-overview-base.component';
import { Dtc } from '../../../../../shared/models/dtc/dtc';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { DtcApiService } from '../../../../services/api/dtc-api.service';
import { DtcQuery } from '../../../../../shared/models/dtc/dtc-query';
import { DtcSelectionTableComponent } from '../../../tables/dtc/dtc-selection-table/dtc-selection-table.component';
import { PaginationService } from '../../../../services/pagination.service';
import { ErrorMessageWithLinkedDtcs } from '../../../../../shared/models/error-messages/error-message-with-linked-dtcs';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { ErrorMessageApiService } from '../../../../services/api/error-message-api.service';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { Decision } from '../../../../../shared/models/internal/decision';
import { Policy } from '../../../../../shared/models/info/policy';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { errorToString } from '../../../../common/common-utils';
import { PropagateLinkedDtcsModal } from '../../../modals/propagate-linked-dtcs-modal/propagate-linked-dtcs-modal';
import { DtcLinkPropagationResult } from '../../../../../shared/models/error-message/dtc-link-propagation-result';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-dtc-selection-overview',
  imports: [
    DtcSelectionTableComponent,
    EnabledByPolicyDirective,
    DecisionModalComponent,
    FloppyIconComponent,
    PropagateLinkedDtcsModal,
    AgentActionDirective,
  ],
  templateUrl: './dtc-selection-overview.component.html',
  styleUrl: './dtc-selection-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PaginationService],
})
export class DtcSelectionOverviewComponent extends CrudOverviewBaseComponent<Dtc> implements OnInit, OnChanges {
  @Input() public errorMessageId?: string;
  @Output() public selectedDtcsChanged: EventEmitter<ErrorMessageWithLinkedDtcs> =
    new EventEmitter<ErrorMessageWithLinkedDtcs>();
  @ViewChild('anotherErrorMessageDecisionModal') public anotherErrorMessageDecisionModal!: DecisionModalComponent;
  @ViewChild('propagateLinkedDtcsModal') public propagateLinkedDtcsModal!: PropagateLinkedDtcsModal;

  public dtcsAssignedToAnotherErrorMessages: string[] = [];
  public dtcSelectionTableReadOnly = false;

  private readonly dtcApi = inject(DtcApiService);
  private readonly errorMessageApi = inject(ErrorMessageApiService);
  private readonly toast = inject(HotToastService);
  private readonly appInsightsService = inject(AppInsightsService);

  private errorMessageWithLinkedDtcs?: ErrorMessageWithLinkedDtcs;
  private lastQuery: DtcQuery = {};

  constructor() {
    super(STORAGE_KEYS.UNSAVED_LINKED_DTCS_CHANGES);
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.accessRightsService.getAccessRights$().subscribe((accessRights) => {
      const noAccessRights = !accessRights.authorized(Policy.canUpdateElevated);
      this.evaluateSwPartNumberState();
      this.dtcSelectionTableReadOnly = noAccessRights || this.swPartNumberIsReadOnly;
    });
    this.appInsightsService.logSidebarUsage('DtcSelectionOverview');
  }

  public ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['errorMessageId'] && this.errorMessageId) {
      if (!this.errorMessageWithLinkedDtcs) {
        this.errorMessageWithLinkedDtcs = {};
      }
      this.errorMessageWithLinkedDtcs.linkedDtcs = [];
      this.errorMessageWithLinkedDtcs.siblingsToLink = [];
      this.errorMessageWithLinkedDtcs.unlinkedDtcs = [];
      this.errorMessageWithLinkedDtcs.siblingsToUnlink = [];
      this.errorMessageWithLinkedDtcs.errorMessageId = this.errorMessageId;
    }
  }

  public onSearchTriggered(query: DtcQuery): void {
    this.lastQuery = query;
    this.items = [];
    this.sidebarOpen = false;
    this.fetchDtcs(query, true);
  }

  public onSortByChanged(query: DtcQuery): void {
    this.onSearchTriggered(query);
  }

  public onScrolledToTheBottom(query: DtcQuery): void {
    this.fetchDtcs(query, false);
  }

  public requestPropagationOfChangesToLinkedDtcs(): void {
    if (this.hasPendingLinkedDtcChanges()) {
      this.propagateLinkedDtcsModal?.openModal(this.errorMessageWithLinkedDtcs);
    }
  }

  public onUpdateSelection(errorMessageWithLinkedDtcs: ErrorMessageWithLinkedDtcs): void {
    this.errorMessageWithLinkedDtcs!.linkedDtcs = errorMessageWithLinkedDtcs.linkedDtcs;
    this.errorMessageWithLinkedDtcs!.unlinkedDtcs = errorMessageWithLinkedDtcs.unlinkedDtcs;
    this.errorMessageWithLinkedDtcs!.siblingsToLink = [];
    this.errorMessageWithLinkedDtcs!.siblingsToUnlink = [];
    this.saveUnsavedChangesToSessionStorage();
  }

  public checkUpdateLinkedDtcs(): void {
    if (
      this.anyDtcIsAssignedToAnotherErrorMessage(
        (this.errorMessageWithLinkedDtcs?.linkedDtcs ?? []).map((dtc) => dtc.id!)
      )
    ) {
      this.anotherErrorMessageDecisionModal.openModal();
    } else {
      this.requestPropagationOfChangesToLinkedDtcs();
    }
  }

  public decisionToUpdateLinkedDtcs(decision: Decision): void {
    if (decision.result === true) {
      this.requestPropagationOfChangesToLinkedDtcs();
    }
  }

  public onLinkedDtcsPropagationConfirmed(propagationInfos: DtcLinkPropagationResult): void {
    this.errorMessageWithLinkedDtcs!.siblingsToLink = propagationInfos.dtcsToLink;
    this.errorMessageWithLinkedDtcs!.siblingsToUnlink = propagationInfos.dtcsToUnlink;
    this.updateLinkedDtcs();
  }

  public onLinkedDtcsPropagationSkipped(): void {
    this.updateLinkedDtcs();
  }

  protected override handleNewSelection(id: string): void {
    throw new Error('Method not implemented.');
  }

  protected override getFriendlyItemName(item?: Dtc | undefined): string {
    throw new Error('Method not implemented.');
  }

  protected override configureDetailsView(item: Dtc): void {
    throw new Error('Method not implemented.');
  }

  private updateLinkedDtcs(): void {
    if (this.errorMessageWithLinkedDtcs) {
      this.errorMessageWithLinkedDtcs.errorMessageId = this.errorMessageId;
      this.errorMessageApi.updateLinkedDtcs(this.errorMessageWithLinkedDtcs).subscribe({
        next: () => {
          this.fetchDtcs(this.lastQuery, true);
          this.selectedDtcsChanged.emit(this.errorMessageWithLinkedDtcs!);
          this.toast.success('Linked DTCs updated successfully.');
          this.clearUnsavedData();
        },
        error: (error) => {
          console.error('Error updating linked DTCs:', errorToString(error));
        },
      });
    }
  }

  private fetchDtcs(query: DtcQuery, resetPagination: boolean): void {
    this.fetchPagedData(resetPagination, (page, pageSize) => {
      query.page = page;
      query.pageSize = pageSize;
      return this.dtcApi.getDtcs(query);
    });
  }

  private anyDtcIsAssignedToAnotherErrorMessage(dtcIds: string[]): boolean {
    const assignedDtcs = this.items?.filter((dtc) => dtcIds.includes(dtc.id!));
    const assignedToAnotherDtcs = assignedDtcs?.filter(
      (dtc) => dtc.errorMessageId && dtc.errorMessageId !== '' && dtc.errorMessageId !== this.errorMessageId
    );
    this.dtcsAssignedToAnotherErrorMessages = assignedToAnotherDtcs?.map((dtc) => dtc.displayId!) ?? [];
    return this.dtcsAssignedToAnotherErrorMessages.length > 0;
  }

  private hasPendingLinkedDtcChanges(): boolean {
    const linkedDtcs = this.errorMessageWithLinkedDtcs?.linkedDtcs ?? [];
    const unlinkedDtcs = this.errorMessageWithLinkedDtcs?.unlinkedDtcs ?? [];

    return linkedDtcs.length > 0 || unlinkedDtcs.length > 0;
  }

  private saveUnsavedChangesToSessionStorage(): void {
    if (this.errorMessageWithLinkedDtcs) {
      this.unsavedChangesService.saveUnsavedChangesToSessionStorage(
        STORAGE_KEYS.UNSAVED_LINKED_DTCS_CHANGES,
        this.errorMessageWithLinkedDtcs
      );
    }
  }
}
